import Big from 'big.js';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';

import { DailyDeploysChart } from '@block-explorer/charts';
import {
  FlexColumn,
  FlexRow,
  formatNumber,
  formatPercentage,
  formatTimestampAge,
  useMatchMedia,
} from '@make-software/cspr-ui';
import {
  BodyText,
  Button,
  HeaderText,
  OrderDirection,
  Tooltip,
} from '@block-explorer/ui';

import { useGetMetrics } from '../../hooks';
import {
  useCurrencyRateAmount,
  useGetPaginatedDailyDeployNumbers,
} from '../../hooks/event-store-hooks';
import FiatCurrency from '../../components/multi-currency/multi-currency';
import { useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';
import { InfoSvgIcon } from '../../components';
import { ANALYTICS_PATH } from '../../router/paths';
import { useCustomHistory } from '../../router/navigation';
import { endOfDay, startOfDay, subDays } from 'date-fns';
import { useGetCasperNetwork } from '../../services/casper-network-context.tsx';

const Container = styled.div(({ theme }) =>
  theme.withMedia({
    height: '100%',
    background: theme.styleguideColors.backgroundPrimary,
    boxShadow: theme.boxShadow.block,
    borderRadius: theme.borderRadius.base,
    padding: ['24px 12px', '28px 28px', '28px 28px', '32px 28px'],
    width: '100%',
    display: 'flex',
    flexDirection: ['column', 'column', 'row'],
    justifyContent: ['center', 'center', 'flex-start'],
    alignItems: 'center',
    '> * + *': {
      marginTop: [20, 20, 0],
      paddingTop: [19, 19, 0],
      marginLeft: [0, 0, 12],
      borderTop: [theme.border.separator, theme.border.separator, 'none'],
    },
  })
);

const MetricMainRow = styled.div(({ theme }) =>
  theme.withMedia({
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: ['column'],
    '> * + *': {
      marginTop: [20, 24],
      paddingTop: [19, 23],
      borderTop: theme.border.separator,
    },
  })
);

const MetricRow = styled.div(({ theme }) =>
  theme.withMedia({
    width: '100%',
    display: 'flex',
    flexDirection: ['column', 'row'],
    justifyContent: ['center', 'flex-start'],
    alignItems: 'center',
    '> * + *': {
      marginTop: [20, 0],
      paddingTop: [19, 0],
      borderTop: [theme.border.separator, 'none'],
    },
  })
);

const MetricBlock = styled.div(({ theme }) =>
  theme.withMedia({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: ['center', 'flex-start'],
    '> * + *': {
      marginTop: [4],
    },
  })
);

const ChartBlock = styled.div(({ theme }) =>
  theme.withMedia({
    display: 'flex',
    width: ['100%', '100%', 324],
    flexDirection: 'column',
    flexShrink: 0,
    alignItems: ['center', 'center', 'flex-start'],
  })
);

const StyledButton = styled(Button)(({ theme }) =>
  theme.withMedia({
    width: ['100%', '100%', '140'],
  })
);

const HomeMetrics = React.memo(() => {
  const { data } = useGetMetrics();
  const { t } = useTranslation();
  const { getLocalisationForNetworkVersion } = useGetCasperNetwork();
  const { homeScene } = getLocalisationForNetworkVersion;

  const history = useCustomHistory();
  const currency = useSelector(
    (state: RootState) => state.currencies.selectedCurrency
  );

  const screenType = useMatchMedia(['mobile', 'tabletOrAbove'], []);
  const btnHeight = useMatchMedia<any>(['36', '36', '24'], []);

  const { data: rateData, loading: loadingRateData } = useCurrencyRateAmount({
    currencyCode: currency.id.toString(),
  });

  const yesterdayTimeStamp = new Date().getTime() - 24 * 60 * 60 * 1000;
  const yesterday = new Date(endOfDay(yesterdayTimeStamp));
  const someDaysAgo = new Date(startOfDay(subDays(yesterday, 13)));

  const { data: dailyDeployNumbers, loading: loadingDailyDeployNumbers } =
    useGetPaginatedDailyDeployNumbers({
      from: someDaysAgo.toISOString(),
      to: yesterday.toISOString(),
      order_direction: OrderDirection.ASC,
      order_by: 'date',
    });

  const csprCurrencyRate = rateData?.data?.amount.toString();

  const apyPercentage =
    data?.totalSupply == null || data?.eraTotalStake == null
      ? null
      : Big(data.totalSupply)
          .mul(0.08)
          .div(data.eraTotalStake)
          .mul(100)
          .toString();

  const eraTotalStakePerc =
    data?.totalSupply == null || data?.eraTotalStake == null
      ? null
      : Big(data.eraTotalStake).div(data.totalSupply).mul(100).toString();

  const marketCap =
    data?.circulatingSupply == null || !csprCurrencyRate
      ? null
      : Big(data.circulatingSupply).mul(csprCurrencyRate).toString();

  const circulatingSupplyPercent =
    data?.totalSupply == null || data?.circulatingSupply == null
      ? null
      : Big(data.circulatingSupply)
          .mul(100)
          .div(Big(data.totalSupply))
          .toNumber();

  const dailyDeploysSeries = dailyDeployNumbers?.data;
  const accTotalDeploys =
    (dailyDeploysSeries &&
      dailyDeploysSeries[dailyDeploysSeries.length - 1]
        ?.total_contract_calls_num +
        dailyDeploysSeries[dailyDeploysSeries.length - 1]
          ?.total_native_transfers_num) ||
    0;

  const isLoading =
    data == null ||
    rateData == null ||
    loadingRateData ||
    dailyDeployNumbers == null ||
    loadingDailyDeployNumbers;

  return (
    <Container>
      <MetricMainRow>
        <MetricRow>
          <MetricBlock>
            <BodyText size={2} variation="gray" uppercase>
              <Trans t={t}>Block Height</Trans>
            </BodyText>
            <HeaderText size={2} loading={isLoading}>
              {data && formatNumber(data.blockHeight)}
            </HeaderText>
            <BodyText size={3} loading={isLoading}>
              {data && formatTimestampAge(data.blockHeightTimestamp)}
            </BodyText>
          </MetricBlock>
          <MetricBlock>
            <BodyText size={2} variation="gray" uppercase>
              <Trans t={t}>Current Validators</Trans>
            </BodyText>
            <HeaderText size={2} loading={isLoading}>
              {data && data.activeValidatorsCount}
            </HeaderText>
            <BodyText size={3} loading={isLoading}>
              {data && data.allActiveBidsCount} <Trans t={t}>active bids</Trans>
            </BodyText>
          </MetricBlock>
          <MetricBlock>
            <BodyText size={2} variation="gray" uppercase>
              <Trans t={t}>Total Stake Bonded</Trans>
            </BodyText>
            <HeaderText size={2} loading={isLoading}>
              {data && formatNumber(data.eraTotalStake)}
            </HeaderText>
            <BodyText size={3} loading={isLoading}>
              {eraTotalStakePerc &&
                formatNumber(eraTotalStakePerc, { precision: 2 })}
              % <Trans t={t}>of Total Supply</Trans>
            </BodyText>
          </MetricBlock>
        </MetricRow>

        <MetricRow>
          <MetricBlock>
            <BodyText size={2} variation="gray" uppercase>
              <Trans t={t}>CSPR Price</Trans>
            </BodyText>
            <HeaderText size={2} loading={isLoading}>
              <FiatCurrency amount={csprCurrencyRate} precision={4} />
            </HeaderText>
            <BodyText size={3} loading={isLoading}>
              <FiatCurrency amount={marketCap} precision={0} />{' '}
              <Trans t={t}>Market Cap</Trans>
            </BodyText>
          </MetricBlock>
          <MetricBlock>
            <BodyText size={2} variation="gray" uppercase>
              <Trans t={t}>Circulating Supply</Trans>
            </BodyText>
            <HeaderText size={2} loading={isLoading}>
              {data && formatNumber(data.circulatingSupply)}
            </HeaderText>
            <BodyText size={3} loading={isLoading}>
              {circulatingSupplyPercent &&
                formatPercentage(circulatingSupplyPercent, { precision: 1 })}
              % <Trans t={t}>of</Trans>{' '}
              {data && `${formatNumber(data.totalSupply)}`}{' '}
              <Trans t={t}>Total</Trans>
            </BodyText>
          </MetricBlock>
          <MetricBlock>
            <FlexRow align={'center'}>
              <BodyText size={2} variation="gray" uppercase>
                <Trans t={t}>APY</Trans>
              </BodyText>
              <Tooltip
                title={t(
                  "Expected annual percentage yield on the staked tokens without taking the validator's fee into account"
                )}
              >
                <InfoSvgIcon
                  src="assets/icons/ic-info.svg"
                  style={{ marginLeft: 6 }}
                />
              </Tooltip>
            </FlexRow>
            <HeaderText size={2} loading={isLoading}>
              {apyPercentage && formatNumber(apyPercentage, { precision: 2 })}%
            </HeaderText>
            <BodyText size={3} loading={isLoading}>
              <Trans t={t}>Annual Percentage Yield</Trans>
            </BodyText>
          </MetricBlock>
        </MetricRow>
      </MetricMainRow>
      <ChartBlock>
        {screenType === 'mobile' ? (
          <FlexColumn itemsSpacing={32}>
            <MetricBlock>
              <BodyText size={2} variation="gray" uppercase>
                {t(homeScene.total)}
              </BodyText>
              <HeaderText size={2} loading={isLoading}>
                {accTotalDeploys && formatNumber(accTotalDeploys)}
              </HeaderText>
            </MetricBlock>
            <BodyText size={2} variation="gray" uppercase noWrap>
              {t(homeScene.past14days)}
            </BodyText>
          </FlexColumn>
        ) : (
          <FlexRow
            justify={screenType === 'mobile' ? 'center' : 'space-between'}
            style={{ width: '100%' }}
          >
            <BodyText size={2} variation="gray" uppercase noWrap>
              {t(homeScene.past14days)}
            </BodyText>
            <FlexRow>
              <BodyText size={2} variation="gray" uppercase>
                <Trans t={t}>TOTAL</Trans>
              </BodyText>
              <BodyText
                size={1}
                variation="black"
                uppercase
                loading={isLoading}
              >
                &nbsp; {accTotalDeploys && formatNumber(accTotalDeploys)}
              </BodyText>
            </FlexRow>
          </FlexRow>
        )}
        {isLoading ? (
          <span>
            <Skeleton width={324} style={{ fontSize: '180px' }} />
          </span>
        ) : (
          <>
            <DailyDeploysChart
              series={[
                {
                  name: t(homeScene.chartDeploysNumber),
                  data: dailyDeploysSeries,
                },
              ]}
              xAxisGetter={(i) => i.date}
              yAxisGetter={(i) => i.contract_calls_num + i.native_transfer_num}
            />
            <StyledButton
              height={btnHeight}
              lineHeight={'sm'}
              color={'secondaryRed'}
              onClick={() => {
                history.push(ANALYTICS_PATH);
              }}
            >
              {t('View more analytics')}
            </StyledButton>
          </>
        )}
      </ChartBlock>
    </Container>
  );
});

export default HomeMetrics;
